import { Button, Checkbox } from '@restomarker/ui-kit';
import { useEffect, useRef, type ReactElement } from 'react';

import { t } from '@/shared/i18n';
import { pushSupported, refreshPushPermission, type PushHere } from '@/shared/push';

import { AlertTiersField } from './AlertTiersField';
import type { AlertTier, PushAlertChannel } from './alertSettingsTypes';
import { useAllowNotifications, usePhoneStatus } from './push';
import styles from './notificationsDialog.module.css';

export interface PushSectionProps {
  push: PushAlertChannel;
  availableTiers: readonly AlertTier[];
  /** Без сети, в саппорт-сессии и пока идёт любое сохранение. */
  disabled: boolean;
  /** Саппорт-сессия: состояние ЭТОГО телефона — платформенного админа, не цели. */
  supportSession: boolean;
  /** Отказ сохранения этого канала — текстом от сервера или «не сохранилось». */
  saveError: string | null;
  onEnabledChange: (enabled: boolean) => void;
  onTiersChange: (tiers: AlertTier[]) => void;
}

/**
 * Push на телефон — выбор человека с сервера (B-039), а не наличие адреса на телефоне.
 *
 * Галочка включает push на ВСЕ телефоны, где человек вошёл, и снятие здесь выключает
 * его везде. Нажатие только сохраняет выбор: этот телефон подключит `usePushSync`,
 * заметив новый выбор, — с запросом разрешения, раз включили отсюда. Выключение адрес
 * FCM установки не гасит.
 *
 * Под галочкой — что с этим телефоном: подключён, Android не показывает уведомления
 * (с путём в настройки и повторным запросом, пока его можно задать), Google не выдал
 * адрес. На планшете Windows и в браузере push нет — там вместо этого подсказка,
 * куда он приходит.
 */
export function PushSection({
  push,
  availableTiers,
  disabled,
  supportSession,
  saveError,
  onEnabledChange,
  onTiersChange,
}: PushSectionProps): ReactElement {
  const allow = useAllowNotifications();

  return (
    <section className={styles.body} aria-label={t('push.section')}>
      <h3 className={styles.sectionTitle}>{t('push.section')}</h3>
      <Checkbox
        checked={push.enabled}
        disabled={disabled}
        onCheckedChange={onEnabledChange}
        label={t('push.toggle')}
        hint={t('push.hint')}
      />
      {push.enabled && !supportSession ? (
        <ThisPhone
          supported={pushSupported()}
          busy={disabled || allow.isPending}
          onAllow={() => allow.mutate()}
        />
      ) : null}
      {push.enabled ? (
        <AlertTiersField
          value={push.tiers}
          available={availableTiers}
          disabled={disabled}
          legend={t('alerts.whenMine')}
          onChange={onTiersChange}
        />
      ) : null}
      {saveError ? (
        <p className={styles.problem} role="alert">
          {saveError}
        </p>
      ) : null}
    </section>
  );
}

interface ThisPhoneProps {
  supported: boolean;
  busy: boolean;
  onAllow: () => void;
}

/** Строка состояния этого телефона. Только при включённом выборе. */
function ThisPhone({ supported, busy, onAllow }: ThisPhoneProps): ReactElement | null {
  const here = usePhoneStatus();
  const problemRef = useRef<HTMLParagraphElement>(null);
  const problem = supported ? problemOf(here) : null;

  // Разрешение могли дать или отнять в настройках Android, пока окно было закрыто или
  // приложение — в фоне: Android приложение не перезапускает, и строка продолжала бы
  // говорить «разрешите» тому, кто уже разрешил (ревью B-039). Видимость и фокус —
  // как на экране принтера: в WebView оболочки одно из событий иногда не приходит.
  useEffect(() => {
    if (!supported) return undefined;
    void refreshPushPermission();
    const refresh = (): void => {
      if (document.visibilityState === 'visible') void refreshPushPermission();
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [supported]);

  // Окно на телефоне выше экрана, и причина — ниже галочки в прокручиваемом теле: без
  // прокрутки к ней человек видел галочку и край окна (Redmi 720×1600, 2026-09-27).
  // `?.` — jsdom прокрутку не реализует.
  useEffect(() => {
    if (problem) problemRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [problem]);

  if (!supported) return <p className={styles.muted}>{t('push.elsewhere')}</p>;
  if (here?.status === 'connected') {
    return <p className={styles.muted}>{t('push.connectedHere')}</p>;
  }
  if (problem === null) return null;

  return (
    <>
      <p ref={problemRef} className={styles.problem} role="alert">
        {t(problem)}
      </p>
      {here?.status === 'blocked' && here.canAsk ? (
        <Button variant="secondary" disabled={busy} onClick={onAllow}>
          {t('push.allow')}
        </Button>
      ) : null}
    </>
  );
}

function problemOf(here: PushHere | null): 'push.blocked' | 'push.failed' | null {
  if (here?.status === 'blocked') return 'push.blocked';
  if (here?.status === 'failed') return 'push.failed';
  return null;
}
